import React, { useEffect, useState } from 'react'
import { IoPhonePortraitOutline } from 'react-icons/io5'
import OTPInput from "otp-input-react"
import useAuth from '../../context/userContext'

const RESEND_COOLDOWN_SECONDS = 45

/**
 * First-time account verification OTP modal.
 * Resend: POST auth/resend-otp/ { email }
 * Verify: POST auth/verify-otp/ { otp_code }
 */
const OTPModal = ({ isOpen, setIsOpen, dismissible = false }) => {
  const { resendOtp, VerifySms, otpLoading, user } = useAuth()
  const [OTPCode, setOTPCODE] = useState("")
  const [seconds, setSeconds] = useState(0)
  const [resendLoading, setResendLoading] = useState(false)
  const [verifyError, setVerifyError] = useState("")

  useEffect(() => {
    if (seconds <= 0) return undefined

    const interval = setInterval(() => {
      setSeconds((prev) => (prev > 0 ? prev - 1 : 0))
    }, 1000)

    return () => clearInterval(interval)
  }, [seconds])

  const resolveEmail = () => {
    try {
      const storedUser = JSON.parse(window.localStorage.getItem("user") || "null")
      const raw =
        user?.email ||
        storedUser?.email ||
        window.localStorage.getItem("email")
      return typeof raw === "string" ? raw.replace(/^"|"$/g, "").trim() : ""
    } catch {
      return ""
    }
  }

  const resendOTP = async () => {
    if (seconds > 0 || resendLoading) return

    setVerifyError("")
    setResendLoading(true)
    try {
      await resendOtp(resolveEmail())
      setOTPCODE("")
      setSeconds(RESEND_COOLDOWN_SECONDS)
    } catch {
      // Error toast handled in resendOtp
    } finally {
      setResendLoading(false)
    }
  }

  const handleVerify = async () => {
    if (OTPCode.length !== 6 || otpLoading) return
    setVerifyError("")
    try {
      await VerifySms(OTPCode)
      setIsOpen?.(false)
    } catch (error) {
      setVerifyError(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Invalid or expired OTP. Use the latest code, or resend."
      )
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] backdrop-blur-md">
      <div className="bg-white dark:bg-dark rounded-lg shadow-lg w-full max-w-[400px] p-4 relative">
        {dismissible && (
          <button
            type="button"
            onClick={() => setIsOpen?.(false)}
            className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 text-sm"
          >
            Close
          </button>
        )}

        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-yellowcolor/10 rounded-full flex items-center justify-center mb-3">
            <div className="w-12 h-12 bg-yellowcolor/30 rounded-full flex items-center justify-center">
              <IoPhonePortraitOutline className="text-yellowcolor" />
            </div>
          </div>

          <h2 className="text-xl font-bold mb-2 dark:text-white">
            Verify Your Account
          </h2>
          <p className="text-gray-600 text-sm dark:text-gray-400 px-2">
            Enter the 6-digit code sent to your email and phone.
            Codes expire in 10 minutes. If you resend, only the newest code will work.
          </p>

          <div className="my-6 w-full">
            <div className="mx-auto items-center justify-center flex">
              <OTPInput
                value={OTPCode}
                onChange={(value) => {
                  setOTPCODE(value)
                  if (verifyError) setVerifyError("")
                }}
                autoFocus
                OTPLength={6}
                otpType="number"
                disabled={otpLoading}
                inputStyles={{
                  borderBottomWidth: 1,
                  borderBottomColor: "gray",
                  padding: 10,
                  margin: 5,
                  width: "100%",
                  borderRadius: "10px",
                  border: "1px solid #E0E0E0",
                  backgroundColor: "#F5F5F5",
                  color: "#000",
                  fontSize: "16px",
                  fontWeight: "bold",
                  textAlign: "center",
                  height: "50px",
                }}
              />
            </div>
            {verifyError && (
              <p className="text-red-500 text-xs mt-3 px-4">{verifyError}</p>
            )}
          </div>

          <div className="flex flex-col gap-4 px-6 w-full">
            <button
              type="button"
              disabled={OTPCode.length !== 6 || otpLoading}
              onClick={handleVerify}
              className={`flex-1 py-3 rounded-full bg-yellowcolor hover:bg-yellowcolor/90 text-black disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {otpLoading ? 'Verifying...' : 'Verify'}
            </button>

            <div className="flex items-center gap-2 justify-center w-full flex-wrap">
              <p className="text-base text-black dark:text-white">
                Didn't receive code?
              </p>
              <button
                type="button"
                className={`${
                  seconds > 0 || resendLoading
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-yellowcolor underline'
                }`}
                onClick={resendOTP}
                disabled={seconds > 0 || resendLoading}
              >
                {resendLoading
                  ? 'Sending...'
                  : seconds > 0
                    ? `Resend in ${seconds}s`
                    : 'Resend'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default OTPModal
